import React, { useState, useEffect } from 'react';
import {
  X,
  Mail,
  Lock,
  Eye,
  EyeOff,
  Check,
  AlertCircle,
  Loader2,
  Sparkles,
  ArrowRight,
  Shield
} from 'lucide-react';
import dashnodeLogo from '../assets/auth/Dashnode_logo_with_name.png';
import cadToCostImg from '../assets/auth/cad_to_cost.svg';
import detailedCostingImg from '../assets/auth/detailed_costing.svg';
import dfmImg from '../assets/auth/dfm.svg';
import { loginWithEmail } from '../services/authService';

const SLIDES = [
  {
    img: cadToCostImg,
    title: 'CAD to cost in seconds',
    description: 'Upload a STEP or CAD file and get an instant machining cost estimate.'
  },
  {
    img: detailedCostingImg,
    title: 'Detailed reports & breakdowns',
    description: 'See costs by operation, raw material, cycle time, and cutter tooling wear.'
  },
  {
    img: dfmImg,
    title: 'Built-in DFM insights',
    description: 'Detect geometric hazards, thin walls, and pocket radii before production.'
  }
];

export default function AuthModal({
  isOpen,
  onClose,
  onAuthSuccess,
  promptContext = null
}) {
  const [activeSlide, setActiveSlide] = useState(0);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [infoMessage, setInfoMessage] = useState('');

  // Auto-advance slides every 4.5 seconds
  useEffect(() => {
    if (!isOpen) return;
    const interval = setInterval(() => {
      setActiveSlide((prev) => (prev + 1) % SLIDES.length);
    }, 4500);
    return () => clearInterval(interval);
  }, [isOpen]);

  if (!isOpen) return null;

  // Form Submit Handler
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password.trim()) {
      setErrorMessage('Please enter both email and password.');
      return;
    }

    setLoading(true);
    setErrorMessage('');
    setInfoMessage('');

    const res = await loginWithEmail(email.trim(), password);
    if (res.success) {
      onAuthSuccess(res.user);
      onClose();
    } else {
      setErrorMessage(res.error || 'Invalid credentials or unauthorized account.');
    }

    setLoading(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-slate-900/60 backdrop-blur-xs animate-in fade-in duration-200">
      <div
        className="bg-white rounded-2xl shadow-2xl border border-slate-200 w-full max-w-4xl overflow-hidden flex flex-col md:flex-row max-h-[92vh] animate-in zoom-in-95 duration-200"
        onClick={(e) => e.stopPropagation()}
      >
        {/* LEFT COLUMN: Feature Slider & Branding (Visible on desktop) */}
        <div className="hidden md:flex md:w-5/12 bg-gradient-to-b from-[#1c70ed] via-[#196ae3] to-[#024dbd] text-white p-7 flex-col justify-between relative overflow-hidden select-none">
          {/* Subtle background glow */}
          <div className="absolute -top-16 -left-16 w-48 h-48 bg-white/10 rounded-full blur-2xl pointer-events-none" />
          <div className="absolute -bottom-16 -right-16 w-48 h-48 bg-blue-300/10 rounded-full blur-2xl pointer-events-none" />

          {/* Top Branding */}
          <div className="relative z-10">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-white/15 backdrop-blur-md flex items-center justify-center border border-white/20">
                <svg
                  className="w-4 h-4 text-white"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="m21.12 6.4-6-3.87a3 3 0 0 0-3.24 0L5.88 6.4a3 3 0 0 0-1.5 2.6v7.74a3 3 0 0 0 1.5 2.6l6 3.87a3 3 0 0 0 3.24 0l6-3.87a3 3 0 0 0 1.5-2.6V9a3 3 0 0 0-1.5-2.6Z" />
                  <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
                  <line x1="12" y1="22.08" x2="12" y2="12" />
                </svg>
              </div>
              <span className="font-bold text-lg tracking-tight">dashnode</span>
              <span className="text-[10px] font-bold bg-white/20 px-1.5 py-0.5 rounded text-blue-100 uppercase">
                Copilot
              </span>
            </div>
            <h2 className="text-xl font-bold mt-4 tracking-tight">
              Welcome to Dashnode
            </h2>
            <p className="text-xs text-blue-100/90 mt-1">
              Autonomous manufacturing engineering at your fingertips.
            </p>
          </div>

          {/* Mid Carousel Visual */}
          <div className="relative z-10 my-4 flex flex-col items-center text-center">
            <div className="w-full h-44 flex items-center justify-center p-2">
              <img
                src={SLIDES[activeSlide].img}
                alt={SLIDES[activeSlide].title}
                className="max-h-40 max-w-full object-contain filter drop-shadow-md transition-all duration-300 transform scale-100"
              />
            </div>
            <h3 className="font-bold text-base mt-2 text-white">
              {SLIDES[activeSlide].title}
            </h3>
            <p className="text-xs text-blue-100/80 mt-1 max-w-xs leading-relaxed">
              {SLIDES[activeSlide].description}
            </p>
          </div>

          {/* Bottom Pagination Dots */}
          <div className="relative z-10 flex items-center justify-center gap-1.5 pt-2">
            {SLIDES.map((_, idx) => (
              <button
                key={idx}
                type="button"
                onClick={() => setActiveSlide(idx)}
                className={`h-1.5 rounded-full transition-all cursor-pointer ${
                  activeSlide === idx ? 'w-6 bg-white' : 'w-2 bg-white/40 hover:bg-white/60'
                }`}
                aria-label={`Slide ${idx + 1}`}
              />
            ))}
          </div>
        </div>

        {/* RIGHT COLUMN: Sign In Form */}
        <div className="flex-1 p-6 sm:p-8 flex flex-col justify-between overflow-y-auto relative bg-white">
          <div>
            {/* Header Bar: Logo & Close */}
            <div className="flex items-center justify-between mb-5">
              <img
                src={dashnodeLogo}
                alt="Dashnode"
                className="h-6 w-auto object-contain"
              />
              <button
                type="button"
                onClick={onClose}
                className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors cursor-pointer"
                title="Close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Prompt context reminder if user typed a message */}
            {promptContext && (
              <div className="mb-4 p-2.5 rounded-xl bg-blue-50/90 border border-blue-200 text-xs text-blue-900 flex items-start gap-2">
                <Sparkles className="w-4 h-4 text-blue-600 shrink-0 mt-0.5" />
                <div className="min-w-0">
                  <p className="font-semibold text-[11px]">
                    Sign in to run your engineering analysis:
                  </p>
                  <p className="text-[11px] text-blue-700 italic truncate mt-0.5">
                    "{promptContext.text || 'Analyze attached CAD file'}"
                  </p>
                </div>
              </div>
            )}

            {/* Selective Access Badge & Heading */}
            <div className="mb-5">
              <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-slate-100 text-slate-700 text-[11px] font-medium border border-slate-200/80 mb-2.5">
                <Shield className="w-3.5 h-3.5 text-blue-600" />
                <span>Authorized Access Only</span>
              </div>
              <h2 className="text-xl font-bold tracking-tight text-slate-900">
                Sign In
              </h2>
              <p className="text-xs text-slate-500 mt-1">
                Enter your authorized credentials to access the manufacturing copilot.
              </p>
            </div>

            {/* Error / Alert Display */}
            {errorMessage && (
              <div className="mb-4 p-2.5 rounded-lg bg-red-50 border border-red-200 text-xs text-red-700 flex items-center gap-2 animate-in fade-in">
                <AlertCircle className="w-4 h-4 shrink-0 text-red-600" />
                <span className="text-[11px] leading-tight">{errorMessage}</span>
              </div>
            )}

            {infoMessage && (
              <div className="mb-4 p-2.5 rounded-lg bg-emerald-50 border border-emerald-200 text-xs text-emerald-800 flex items-center gap-2">
                <Check className="w-4 h-4 shrink-0 text-emerald-600" />
                <span className="text-[11px]">{infoMessage}</span>
              </div>
            )}

            {/* Email & Password Form */}
            <form onSubmit={handleSubmit} className="space-y-3.5">
              {/* Email Field */}
              <div>
                <label className="block text-[11px] font-semibold text-slate-700 mb-1">
                  Email Address <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Mail className="w-4 h-4 text-slate-400 absolute left-3 top-2.5 pointer-events-none" />
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="name@company.com"
                    className="w-full pl-9 pr-3 py-2 text-xs bg-slate-50 border border-slate-200 rounded-xl text-slate-800 placeholder-slate-400 focus:bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-400 transition-colors shadow-2xs"
                  />
                </div>
              </div>

              {/* Password Field */}
              <div>
                <label className="block text-[11px] font-semibold text-slate-700 mb-1">
                  Password <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Lock className="w-4 h-4 text-slate-400 absolute left-3 top-2.5 pointer-events-none" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter password"
                    className="w-full pl-9 pr-9 py-2 text-xs bg-slate-50 border border-slate-200 rounded-xl text-slate-800 placeholder-slate-400 focus:bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-400 transition-colors shadow-2xs"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-2.5 text-slate-400 hover:text-slate-600 cursor-pointer"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {/* Remember Me / Forgot Password */}
              <div className="flex items-center justify-between text-xs pt-0.5">
                <label className="flex items-center gap-1.5 cursor-pointer select-none text-slate-600 text-[11px]">
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span>Remember me</span>
                </label>
                <button
                  type="button"
                  onClick={() =>
                    setInfoMessage('Please contact your administrator to reset your password.')
                  }
                  className="text-[11px] text-blue-600 hover:underline cursor-pointer"
                >
                  Forgot password?
                </button>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loading}
                className="w-full py-2.5 px-4 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-xs font-semibold shadow-xs transition-all active:scale-[0.99] flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50 mt-3"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    <span>Signing in...</span>
                  </>
                ) : (
                  <>
                    <span>Sign In</span>
                    <ArrowRight className="w-3.5 h-3.5" />
                  </>
                )}
              </button>
            </form>
          </div>

          {/* Footer Notice */}
          <div className="pt-6 border-t border-slate-100 mt-6 text-center">
            <p className="text-[11px] text-slate-400">
              Need access? Contact your Dashnode administrator for credentials.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
